import React, { createContext, useContext, useState } from "react";
import { Auth0Provider, useAuth0 as useRealAuth0 } from "@auth0/auth0-react";

// Detect whether Auth0 has been configured via environment variables.
const domain = process.env.REACT_APP_AUTH0_DOMAIN;
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
const authConfigured = !!(domain && clientId);

const MockContext = createContext({
  isAuthenticated: false,
  isLoading: false,
  user: null,
  loginWithRedirect: async () => {},
  logout: () => {},
  getAccessTokenSilently: async () => "",
  handleRedirectCallback: async () => {},
});

function MockAuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  const loginWithRedirect = async () => {
    setIsAuthenticated(true);
    setUser({ email: "dev@example.com" });
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
  };

  const getAccessTokenSilently = async () => "dev-token";
  const handleRedirectCallback = async () => {};

  const value = {
    isAuthenticated,
    isLoading: false,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    handleRedirectCallback,
  };

  return <MockContext.Provider value={value}>{children}</MockContext.Provider>;
}

function RealAuthProvider({ children }) {
  const onRedirectCallback = (appState) => {
    const target = appState?.returnTo || "/";
    window.history.replaceState({}, document.title, target);
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience,
        scope: "openid profile email",
      }}
    >
      {children}
    </Auth0Provider>
  );
}

export function AuthProvider({ children }) {
  return authConfigured ? (
    <RealAuthProvider>{children}</RealAuthProvider>
  ) : (
    <MockAuthProvider>{children}</MockAuthProvider>
  );
}

function useMockAuth() {
  return useContext(MockContext);
}

function useRealAuth() {
  return useRealAuth0();
}

export function useAuth() {
  // Keep hook calls unconditional by delegating to separate hooks,
  // then selecting the *result*.
  const real = useRealAuth();
  const mock = useMockAuth();
  return authConfigured ? real : mock;
}
